import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

const STATUS_CFG = {
  full: {
    label: "Full Day",
    short: "Full",
    color: "#059669",
    bg: "#D1FAE5",
    border: "#A7F3D0",
    activeBg: "#059669",
  },
  half: {
    label: "Half Day",
    short: "Half",
    color: "#D97706",
    bg: "#FEF3C7",
    border: "#FDE68A",
    activeBg: "#D97706",
  },
  overtime: {
    label: "Overtime",
    short: "OT",
    color: "#0369A1",
    bg: "#DBEAFE",
    border: "#93C5FD",
    activeBg: "#0369A1",
  },
  absent: {
    label: "Absent",
    short: "Absent",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    activeBg: "#DC2626",
  },
  holiday: {
    label: "Holiday",
    short: "Hol",
    color: "#B45309",
    bg: "#FEF3C7",
    border: "#FDE68A",
    activeBg: "#B45309",
  },
  leave: {
    label: "Leave",
    short: "Leave",
    color: "#7C3AED",
    bg: "#EDE9FE",
    border: "#DDD6FE",
    activeBg: "#7C3AED",
  },
};

export default function Attendance() {
  const navigate = useNavigate();
  const [labours, setLabours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState({}); // { labourId: status | "loading" }
  const [otHours, setOtHours] = useState({}); // { labourId: number }
  const [otInput, setOtInput] = useState({}); // { labourId: bool } — show OT input?
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const _now = new Date();
  const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;
  const todayDisplay = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const navItems = [
    { label: "Dashboard", emoji: "🏠", path: "/carpenter/dashboard" },
    { label: "My Labours", emoji: "👷", path: "/carpenter/labours" },
    { label: "Add Labour", emoji: "➕", path: "/carpenter/add-labour" },
    {
      label: "Attendance",
      emoji: "📋",
      path: "/carpenter/attendance",
      active: true,
    },
    { label: "Payments", emoji: "💰", path: "/carpenter/payments" },
    { label: "Salary", emoji: "💵", path: "/carpenter/salary" },
  ];

  // ── Fetch ───────────────────────────────────────────────────────
  const fetchLabours = async () => {
    setLoading(true);
    try {
      const res = await api.get("/carpenter/labours");
      const data = res.data.data || [];
      setLabours(data);
      try {
        const att = await api.get("/attendance/today");
        const attMap = {},
          otMap = {};
        (att.data.records || []).forEach((r) => {
          attMap[r.labourId] = r.status;
          if (r.overtimeHours) otMap[r.labourId] = r.overtimeHours;
        });
        setMarking(attMap);
        setOtHours(otMap);
      } catch {
        /* no attendance yet */
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabours();
  }, []);

  // ── Mark single ─────────────────────────────────────────────────
  const markAttendance = async (labourId, status, hours) => {
    setMarking((prev) => ({ ...prev, [labourId]: "loading" }));
    try {
      const payload = { labourId, date: today, status };
      if (status === "overtime") payload.overtimeHours = Number(hours) || 1;
      await api.post("/attendance/mark", payload);
      setMarking((prev) => ({ ...prev, [labourId]: status }));
      if (status === "overtime")
        setOtHours((prev) => ({ ...prev, [labourId]: Number(hours) || 1 }));
      // hide OT input after saving
      setOtInput((prev) => ({ ...prev, [labourId]: false }));
    } catch (err) {
      console.error(err?.response?.data?.message || err.message);
      setMarking((prev) => {
        const n = { ...prev };
        delete n[labourId];
        return n;
      });
    }
  };

  // ── Mark all ────────────────────────────────────────────────────
  const markAll = async (status) => {
    setMarkingAll(true);
    for (const l of labours)
      await markAttendance(l._id, status, otHours[l._id]);
    setMarkingAll(false);
  };

  // ── Overtime button click — show input first ─────────────────────
  const handleOTClick = (labourId) => {
    const current = marking[labourId];
    if (current === "overtime") {
      // already OT — toggle input visibility
      setOtInput((prev) => ({ ...prev, [labourId]: !prev[labourId] }));
    } else {
      // first click — show input
      setOtInput((prev) => ({ ...prev, [labourId]: true }));
      setOtHours((prev) => ({ ...prev, [labourId]: prev[labourId] || 1 }));
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────
  const initials = (n) =>
    n
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const avatarColors = [
    "#D97706",
    "#0369A1",
    "#059669",
    "#7C3AED",
    "#DC2626",
    "#0891B2",
    "#B45309",
    "#065F46",
  ];
  const avatarColor = (id) =>
    avatarColors[id?.charCodeAt(0) % avatarColors.length] || "#D97706";

  const activeLabourIds = labours.map((l) => l._id);

  const counts = activeLabourIds.reduce((acc, id) => {
    const s = marking[id];
    if (s && s !== "loading") {
      acc[s] = (acc[s] || 0) + 1;
    }
    return acc;
  }, {});

  const markedCount = activeLabourIds.filter((id) => {
    const s = marking[id];
    return s && s !== "loading";
  }).length;
  const pct = labours.length
    ? Math.round((markedCount / labours.length) * 100)
    : 0;

  // Badge display for a status
  const StatusBadge = ({ status, hours }) => {
    const cfg = STATUS_CFG[status];
    if (!cfg) return null;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "11px",
          fontWeight: "700",
          padding: "3px 10px",
          borderRadius: "20px",
          background: cfg.bg,
          color: cfg.color,
          border: `1px solid ${cfg.border}`,
        }}
      >
        {cfg.label}
        {status === "overtime" && hours ? ` (${hours}h)` : ""}
      </span>
    );
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        @keyframes shimmer{ from{background-position:200% center} to{background-position:-200% center} }
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Plus Jakarta Sans',sans-serif; background:#FAF7F4; }

        .sidebar { width:220px; flex-shrink:0; background:#fff; border-right:1.5px solid #E7E5E4; padding:24px 14px; display:flex; flex-direction:column; gap:6px; position:sticky; top:0; height:100vh; overflow-y:auto; }
        .topbar  { display:none; }
        .hamburger { display:none; align-items:center; justify-content:center; width:40px; height:40px; border-radius:10px; border:1.5px solid #E7E5E4; background:#fff; cursor:pointer; flex-shrink:0; }
        .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:40; }
        .nav-btn { display:flex; align-items:center; gap:9px; width:100%; padding:10px 12px; border-radius:10px; border:none; background:none; font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; font-weight:600; color:#78716C; cursor:pointer; transition:background .15s,color .15s; text-align:left; }
        .nav-btn:hover,.nav-btn.active { background:#FEF3C7; color:#D97706; }
        .nav-btn.danger:hover { background:#FEF2F2; color:#DC2626; }

        /* Status button strip */
        .status-strip { display:grid; grid-template-columns:repeat(6,1fr); border-top:1.5px solid #F0EFEE; }
        .s-btn { padding:9px 4px; border:none; background:#FAFAF9; font-family:'Plus Jakarta Sans',sans-serif; cursor:pointer; transition:all .15s; display:flex; flex-direction:column; align-items:center; gap:2px; border-right:1px solid #F0EFEE; color:#78716C; }
        .s-btn:last-child { border-right:none; }
        .s-btn .s-icon { font-size:14px; line-height:1; }
        .s-btn .s-lbl  { font-size:10px; font-weight:700; }
        .s-btn:hover { filter:brightness(.94); }
        .s-btn.act-full     { background:#059669 !important; color:#fff !important; }
        .s-btn.act-half     { background:#D97706 !important; color:#fff !important; }
        .s-btn.act-overtime { background:#0369A1 !important; color:#fff !important; }
        .s-btn.act-absent   { background:#DC2626 !important; color:#fff !important; }
        .s-btn.act-holiday  { background:#B45309 !important; color:#fff !important; }
        .s-btn.act-leave    { background:#7C3AED !important; color:#fff !important; }

        /* Labour card */
        .l-card { background:#fff; border:1.5px solid #E7E5E4; border-radius:18px; overflow:hidden; transition:box-shadow .2s,transform .2s; animation:fadeUp .4s ease both; }
        .l-card:hover { box-shadow:0 8px 28px rgba(0,0,0,.07); transform:translateY(-2px); }

        /* OT input row */
        .ot-row { display:flex; align-items:center; gap:8px; padding:10px 14px; background:#EFF6FF; border-top:1px solid #BFDBFE; }
        .ot-input { width:64px; padding:6px 10px; border:1.5px solid #93C5FD; border-radius:8px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:700; color:#0369A1; background:#fff; outline:none; text-align:center; }
        .ot-save  { padding:6px 14px; background:#0369A1; border:none; border-radius:8px; font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700; color:#fff; cursor:pointer; white-space:nowrap; }
        .ot-save:hover { background:#0284C7; }

        /* Mark-all button */
        .mall-btn { display:flex; align-items:center; gap:7px; padding:8px 14px; border-radius:10px; border:1.5px solid; font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700; cursor:pointer; transition:all .15s; white-space:nowrap; }
        .mall-btn:hover { transform:translateY(-1px); box-shadow:0 4px 10px rgba(0,0,0,.1); }

        /* Progress bar */
        .prog-fill { height:100%; border-radius:99px; background:linear-gradient(90deg,#92400E,#D97706,#FCD34D); background-size:200% auto; animation:shimmer 2s linear infinite; transition:width .6s cubic-bezier(.4,0,.2,1); }

        /* Desktop table row */
        .att-row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:13px 18px; border-bottom:1px solid #F5F5F4; transition:background .12s; flex-wrap:wrap; }
        .att-row:hover { background:#FFFBF7; }
        .att-row:last-child { border-bottom:none; }

        .att-cards { display:none; }

        @media (max-width:768px) {
          .sidebar { position:fixed; left:0; top:0; z-index:50; transform:translateX(-100%); transition:transform .28s; height:100%; }
          .sidebar.open { transform:translateX(0); }
          .sidebar-overlay.open { display:block; }
          .topbar { display:flex; align-items:center; gap:12px; padding:14px 16px; background:#fff; border-bottom:1.5px solid #E7E5E4; position:sticky; top:0; z-index:30; }
          .hamburger { display:flex; }
          .main-wrap { flex-direction:column !important; }
          .main-content { padding:16px !important; }
          .desktop-table { display:none !important; }
          .att-cards { display:flex !important; flex-direction:column; gap:12px; }
          .stats-row { grid-template-columns:1fr 1fr 1fr !important; }
          .markall-row { flex-wrap:wrap !important; }
          .status-strip { grid-template-columns:repeat(3,1fr) !important; }
          .s-btn { border-bottom:1px solid #F0EFEE; }
        }
        @media (min-width:769px) and (max-width:1100px) {
          .sidebar { width:190px; }
          .cards-grid { grid-template-columns:1fr 1fr !important; }
        }
        @media (max-width:420px) {
          .main-content { padding:12px !important; }
          .stats-row { grid-template-columns:1fr 1fr !important; }
        }
      `}</style>

      <div
        className="main-wrap"
        style={{
          display: "flex",
          minHeight: "100vh",
          fontFamily: "'Plus Jakarta Sans',sans-serif",
          backgroundColor: "#FAF7F4",
        }}
      >
        <div
          className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* ── SIDEBAR ── */}
        <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "28px",
              padding: "0 4px",
            }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "linear-gradient(135deg,#92400E,#D97706)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: "18px" }}>🪚</span>
            </div>
            <div>
              <p
                style={{
                  fontWeight: "800",
                  fontSize: "15px",
                  color: "#1C1917",
                }}
              >
                Craftigo
              </p>
              <p
                style={{
                  fontSize: "11px",
                  color: "#A8A29E",
                  fontWeight: "500",
                }}
              >
                Carpenter Panel
              </p>
            </div>
          </div>
          <p
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: "#C4B5A4",
              letterSpacing: "0.7px",
              textTransform: "uppercase",
              padding: "0 6px",
              marginBottom: "6px",
            }}
          >
            Menu
          </p>
          {navItems.map((n) => (
            <button
              key={n.label}
              className={`nav-btn ${n.active ? "active" : ""}`}
              onClick={() => {
                navigate(n.path);
                setSidebarOpen(false);
              }}
            >
              {n.emoji} {n.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <div
            style={{
              padding: "12px",
              background: "linear-gradient(135deg,#FEF3C7,#FFF7ED)",
              borderRadius: "12px",
              border: "1px solid #FDE68A",
              marginBottom: "10px",
            }}
          >
            <p
              style={{ fontSize: "12px", fontWeight: "700", color: "#92400E" }}
            >
              🪚 Carpenter
            </p>
            <p style={{ fontSize: "11px", color: "#B45309", marginTop: "2px" }}>
              Manage your team
            </p>
          </div>
          <button
            className="nav-btn danger"
            onClick={() => {
              localStorage.clear();
              navigate("/login");
            }}
          >
            🚪 Logout
          </button>
        </aside>

        {/* ── RIGHT ── */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Mobile topbar */}
          <div className="topbar">
            <button className="hamburger" onClick={() => setSidebarOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line
                  x1="3"
                  y1="6"
                  x2="21"
                  y2="6"
                  stroke="#44403C"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="3"
                  y1="12"
                  x2="21"
                  y2="12"
                  stroke="#44403C"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="3"
                  y1="18"
                  x2="21"
                  y2="18"
                  stroke="#44403C"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <span
              style={{ fontWeight: "800", fontSize: "16px", color: "#1C1917" }}
            >
              📋 Attendance
            </span>
          </div>

          <main
            className="main-content"
            style={{ padding: "28px 32px", flex: 1, overflowY: "auto" }}
          >
            {/* ── PAGE HEADER ── */}
            <div
              style={{
                marginBottom: "20px",
                animation: "fadeUp .35s ease both",
              }}
            >
              <h1
                style={{
                  fontSize: "26px",
                  fontWeight: "800",
                  color: "#1C1917",
                  letterSpacing: "-0.5px",
                }}
              >
                Attendance
              </h1>
              <p
                style={{ fontSize: "13px", color: "#A8A29E", marginTop: "3px" }}
              >
                📅 {todayDisplay}
              </p>
            </div>

            {/* ── STATS ROW ── */}
            <div
              className="stats-row"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6,1fr)",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              {Object.entries(STATUS_CFG).map(([key, cfg], i) => (
                <div
                  key={key}
                  style={{
                    background: cfg.bg,
                    border: `1.5px solid ${cfg.border}`,
                    borderRadius: "12px",
                    padding: "10px 12px",
                    animation: `fadeUp .4s ${i * 0.04}s ease both`,
                  }}
                >
                  <p
                    style={{
                      fontSize: "20px",
                      fontWeight: "800",
                      color: cfg.color,
                      lineHeight: "1",
                    }}
                  >
                    {counts[key] || 0}
                  </p>
                  <p
                    style={{
                      fontSize: "10px",
                      color: "#78716C",
                      fontWeight: "700",
                      marginTop: "3px",
                    }}
                  >
                    {cfg.label}
                  </p>
                </div>
              ))}
            </div>

            {/* ── PROGRESS + MARK ALL ── */}
            <div
              style={{
                background: "#fff",
                border: "1.5px solid #E7E5E4",
                borderRadius: "16px",
                padding: "16px 20px",
                marginBottom: "20px",
                animation: "fadeUp .4s .2s ease both",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#1C1917",
                  }}
                >
                  Today's Progress
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#A8A29E",
                      fontWeight: "500",
                      marginLeft: "8px",
                    }}
                  >
                    {markedCount}/{labours.length} marked
                  </span>
                </span>
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: "800",
                    color: pct === 100 ? "#059669" : "#D97706",
                  }}
                >
                  {pct}%
                </span>
              </div>
              <div
                style={{
                  height: "9px",
                  background: "#F3F4F6",
                  borderRadius: "99px",
                  overflow: "hidden",
                  marginBottom: "14px",
                }}
              >
                <div className="prog-fill" style={{ width: `${pct}%` }} />
              </div>
              <div
                className="markall-row"
                style={{ display: "flex", gap: "8px" }}
              >
                {[
                  {
                    s: "full",
                    label: "All Full Day",
                    bg: "#D1FAE5",
                    bc: "#A7F3D0",
                    tc: "#059669",
                  },
                  {
                    s: "absent",
                    label: "All Absent",
                    bg: "#FEF2F2",
                    bc: "#FECACA",
                    tc: "#DC2626",
                  },
                  {
                    s: "holiday",
                    label: "All Holiday",
                    bg: "#FEF3C7",
                    bc: "#FDE68A",
                    tc: "#B45309",
                  },
                ].map((b) => (
                  <button
                    key={b.s}
                    className="mall-btn"
                    disabled={markingAll || labours.length === 0}
                    onClick={() => markAll(b.s)}
                    style={{
                      background: b.bg,
                      borderColor: b.bc,
                      color: b.tc,
                      opacity: markingAll ? 0.6 : 1,
                    }}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── DESKTOP TABLE ── */}
            <div
              className="desktop-table"
              style={{
                background: "#fff",
                border: "1.5px solid #E7E5E4",
                borderRadius: "16px",
                overflow: "hidden",
                animation: "fadeUp .4s .25s ease both",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 18px",
                  background: "#FAF7F4",
                  borderBottom: "1.5px solid #E7E5E4",
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#A8A29E",
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                  }}
                >
                  Labour
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#A8A29E",
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                  }}
                >
                  Mark Attendance
                </span>
              </div>

              {loading ? (
                <div style={{ padding: "48px", textAlign: "center" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      border: "3px solid #FDE68A",
                      borderTop: "3px solid #D97706",
                      borderRadius: "50%",
                      animation: "spin .7s linear infinite",
                      margin: "0 auto 12px",
                    }}
                  />
                  <p style={{ color: "#A8A29E", fontSize: "14px" }}>
                    Loading labours…
                  </p>
                </div>
              ) : labours.length === 0 ? (
                <div style={{ padding: "48px", textAlign: "center" }}>
                  <p style={{ fontSize: "32px", marginBottom: "10px" }}>👷</p>
                  <p
                    style={{
                      color: "#78716C",
                      fontWeight: "600",
                      fontSize: "15px",
                    }}
                  >
                    No labours yet
                  </p>
                  <button
                    onClick={() => navigate("/carpenter/add-labour")}
                    style={{
                      marginTop: "14px",
                      padding: "10px 20px",
                      background: "#D97706",
                      border: "none",
                      borderRadius: "10px",
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    ➕ Add Labour
                  </button>
                </div>
              ) : (
                labours.map((l) => {
                  const status = marking[l._id];
                  const loading = status === "loading";
                  const c = avatarColor(l._id);
                  const showOT = otInput[l._id];
                  return (
                    <div key={l._id} className="att-row">
                      {/* Left: info */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          minWidth: "180px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "12px",
                            background: `${c}18`,
                            border: `1.5px solid ${c}35`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "800",
                            color: c,
                            flexShrink: 0,
                          }}
                        >
                          {initials(l.name)}
                        </div>
                        <div>
                          <p
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#1C1917",
                            }}
                          >
                            {l.name}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginTop: "2px",
                            }}
                          >
                            <p style={{ fontSize: "11px", color: "#A8A29E" }}>
                              ₹{l.dailyRate ?? "—"}/day
                            </p>
                            {status && status !== "loading" && (
                              <StatusBadge
                                status={status}
                                hours={otHours[l._id]}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: buttons + history */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {loading ? (
                          <div
                            style={{
                              width: "22px",
                              height: "22px",
                              border: "2.5px solid #FDE68A",
                              borderTop: "2.5px solid #D97706",
                              borderRadius: "50%",
                              animation: "spin .7s linear infinite",
                            }}
                          />
                        ) : (
                          <>
                            {/* 6 status buttons */}
                            <div
                              style={{
                                display: "flex",
                                gap: "5px",
                                flexWrap: "wrap",
                              }}
                            >
                              {Object.entries(STATUS_CFG).map(([key, cfg]) => (
                                <button
                                  key={key}
                                  onClick={() =>
                                    key === "overtime"
                                      ? handleOTClick(l._id)
                                      : markAttendance(l._id, key)
                                  }
                                  style={{
                                    padding: "6px 11px",
                                    borderRadius: "8px",
                                    border: `1.5px solid ${status === key ? cfg.activeBg : cfg.border}`,
                                    background:
                                      status === key ? cfg.activeBg : cfg.bg,
                                    fontFamily:
                                      "'Plus Jakarta Sans',sans-serif",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    color: status === key ? "#fff" : cfg.color,
                                    cursor: "pointer",
                                    transition: "all .15s",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {cfg.label}
                                  {key === "overtime" &&
                                  status === "overtime" &&
                                  otHours[l._id]
                                    ? ` (${otHours[l._id]}h)`
                                    : ""}
                                </button>
                              ))}
                            </div>
                            {/* OT hours inline input */}
                            {showOT && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "5px 10px",
                                  background: "#EFF6FF",
                                  borderRadius: "10px",
                                  border: "1.5px solid #BFDBFE",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    color: "#0369A1",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  OT hrs:
                                </span>
                                <input
                                  type="number"
                                  min="0.5"
                                  max="12"
                                  step="0.5"
                                  value={otHours[l._id] || 1}
                                  onChange={(e) =>
                                    setOtHours((p) => ({
                                      ...p,
                                      [l._id]: e.target.value,
                                    }))
                                  }
                                  className="ot-input"
                                  style={{ width: "52px" }}
                                />
                                <button
                                  className="ot-save"
                                  onClick={() =>
                                    markAttendance(
                                      l._id,
                                      "overtime",
                                      otHours[l._id] || 1,
                                    )
                                  }
                                >
                                  Save
                                </button>
                              </div>
                            )}
                            <div
                              style={{
                                width: "1px",
                                height: "28px",
                                background: "#E7E5E4",
                                flexShrink: 0,
                              }}
                            />
                            <button
                              onClick={() =>
                                navigate(
                                  `/carpenter/labour-attendance/${l._id}`,
                                )
                              }
                              style={{
                                padding: "7px 12px",
                                background: "#F5F3FF",
                                border: "1.5px solid #DDD6FE",
                                borderRadius: "8px",
                                fontFamily: "'Plus Jakarta Sans',sans-serif",
                                fontSize: "11px",
                                fontWeight: "700",
                                color: "#7C3AED",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              📊 History
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ── MOBILE CARDS ── */}
            <div className="att-cards">
              {loading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      border: "3px solid #FDE68A",
                      borderTop: "3px solid #D97706",
                      borderRadius: "50%",
                      animation: "spin .7s linear infinite",
                      margin: "0 auto",
                    }}
                  />
                </div>
              ) : (
                labours.map((l) => {
                  const status = marking[l._id];
                  const c = avatarColor(l._id);
                  const showOT = otInput[l._id];
                  return (
                    <div key={l._id} className="l-card">
                      {/* Status colour strip */}
                      <div
                        style={{
                          height: "4px",
                          background: STATUS_CFG[status]?.activeBg || "#E7E5E4",
                          transition: "background .3s",
                        }}
                      />

                      <div style={{ padding: "14px 14px 0" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "12px",
                              background: `${c}18`,
                              border: `1.5px solid ${c}35`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "15px",
                              fontWeight: "800",
                              color: c,
                              flexShrink: 0,
                            }}
                          >
                            {initials(l.name)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#1C1917",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {l.name}
                            </p>
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#A8A29E",
                                marginTop: "1px",
                              }}
                            >
                              ₹{l.dailyRate ?? "—"}/day
                            </p>
                          </div>
                          {status && status !== "loading" && (
                            <StatusBadge
                              status={status}
                              hours={otHours[l._id]}
                            />
                          )}
                        </div>
                      </div>

                      {/* Status strip — 3+3 grid on mobile */}
                      <div className="status-strip">
                        {Object.entries(STATUS_CFG).map(([key, cfg]) => (
                          <button
                            key={key}
                            className={`s-btn ${status === key ? `act-${key}` : ""}`}
                            onClick={() =>
                              key === "overtime"
                                ? handleOTClick(l._id)
                                : markAttendance(l._id, key)
                            }
                            disabled={status === "loading"}
                          >
                            <span className="s-icon">
                              {key === "full"
                                ? "✓"
                                : key === "half"
                                  ? "½"
                                  : key === "overtime"
                                    ? "⏱"
                                    : key === "absent"
                                      ? "✗"
                                      : key === "holiday"
                                        ? "★"
                                        : "✈"}
                            </span>
                            <span className="s-lbl">
                              {cfg.label}
                              {key === "overtime" &&
                              status === "overtime" &&
                              otHours[l._id]
                                ? ` ${otHours[l._id]}h`
                                : ""}
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* OT input row */}
                      {showOT && (
                        <div className="ot-row">
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "700",
                              color: "#0369A1",
                            }}
                          >
                            Overtime Hours:
                          </span>
                          <input
                            type="number"
                            min="0.5"
                            max="12"
                            step="0.5"
                            value={otHours[l._id] || 1}
                            onChange={(e) =>
                              setOtHours((p) => ({
                                ...p,
                                [l._id]: e.target.value,
                              }))
                            }
                            className="ot-input"
                          />
                          <button
                            className="ot-save"
                            onClick={() =>
                              markAttendance(
                                l._id,
                                "overtime",
                                otHours[l._id] || 1,
                              )
                            }
                          >
                            Save OT
                          </button>
                        </div>
                      )}

                      {/* History */}
                      <div style={{ padding: "0 14px 12px" }}>
                        <button
                          onClick={() =>
                            navigate(`/carpenter/labour-attendance/${l._id}`)
                          }
                          style={{
                            width: "100%",
                            marginTop: "10px",
                            padding: "9px",
                            background: "#F5F3FF",
                            border: "1.5px solid #DDD6FE",
                            borderRadius: "10px",
                            fontFamily: "'Plus Jakarta Sans',sans-serif",
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "#7C3AED",
                            cursor: "pointer",
                          }}
                        >
                          📊 View Attendance History
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
